import { useRef, useState } from 'react';

import { Button, Wrapper } from '@american-autonomy/ag-ui';

import Combobox from 'components/Combobox';
import CloseIcon from 'components/icons/CloseIcon';
import SearchIcon from 'components/icons/SearchIcon';

import useGeocode from './hooks/useGeocode';
import useTranslation from './hooks/useTranslation';

import SearchContainer from './components/SearchContainer';
import { TEST_ID } from './constants';
import type { FunctionComponent, GeocodeResult, Item, Props } from './types';

import styles from './styles.module.scss';

const MapAddressSearch: FunctionComponent<Props> = ({
    className = '',
    onClearAddress,
    onSelectAddress,
    testId = TEST_ID
}) => {
    const { NO_RESULTS, PLACEHOLDER } = useTranslation();
    const { fetchSuggestions, isLoading } = useGeocode();

    const resultsRef = useRef<Map<string, GeocodeResult>>(new Map());

    const [searchKey, setSearchKey] = useState(0);
    const [hasValue, setHasValue] = useState(false);

    const handleAsyncFilter = async (searchTerm: string): Promise<Item[]> => {
        const results = await fetchSuggestions({ query: searchTerm });

        resultsRef.current = new Map(results.map(result => [result.id, result]));

        return results.map(({ id, label }) => ({ label, value: id }));
    };

    const handleValid = (value: string) => {
        const result = resultsRef.current.get(value);

        if (!result) {
            return;
        }

        onSelectAddress({ center: result.center });
    };

    const handleChange = (value: string) => {
        setHasValue(Boolean(value));
    };

    const handleClear = () => {
        setSearchKey(key => key + 1);
        setHasValue(false);
        onClearAddress?.();
    };

    return (
        <Wrapper className={`${styles.mapAddressSearch} ${className}`} testId={testId}>
            <SearchContainer>
                <SearchIcon className={styles.searchIcon} />

                <Combobox
                    asyncFilter={handleAsyncFilter}
                    className={styles.combobox}
                    isLoading={isLoading}
                    key={searchKey}
                    noResultsText={NO_RESULTS}
                    onChange={handleChange}
                    onValid={handleValid}
                    placeholder={PLACEHOLDER}
                />

                {hasValue && (
                    <Button className={styles.button} onClick={handleClear}>
                        <CloseIcon className={styles.closeIcon} />
                    </Button>
                )}
            </SearchContainer>
        </Wrapper>
    );
};

export default MapAddressSearch;
